/**
 * Outbound links for a clicked point: Google Street View, Rightmove, Zoopla.
 *
 * Rightmove and Zoopla both take a drawn search area as a Google encoded
 * polyline (lat/lon at five decimals, zig-zag deltas, five-bit chunks offset by
 * 63). They differ only in the wrapping:
 *
 *   rightmove  locationIdentifier = USERDEFINEDAREA^{"polylines":"<poly>"},
 *              URL-encoded once; the ring is closed (first point repeated)
 *   zoopla     polyenc = <poly>, URL-encoded twice; the ring is left open
 */

/** The search area drawn around a click, in metres. */
export const SEARCH_RADIUS_M = 250;
const SEARCH_VERTICES = 16;

export interface LatLon {
  lat: number;
  lon: number;
}

function encodeValue(delta: number): string {
  let v = delta < 0 ? ~(delta << 1) : delta << 1;
  let out = "";
  while (v >= 0x20) {
    out += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
    v >>= 5;
  }
  return out + String.fromCharCode(v + 63);
}

/** Google's encoded polyline format. */
export function encodePolyline(points: LatLon[]): string {
  let lat = 0;
  let lon = 0;
  let out = "";
  for (const p of points) {
    const la = Math.round(p.lat * 1e5);
    const lo = Math.round(p.lon * 1e5);
    out += encodeValue(la - lat) + encodeValue(lo - lon);
    lat = la;
    lon = lo;
  }
  return out;
}

export function decodePolyline(text: string): LatLon[] {
  const out: LatLon[] = [];
  let i = 0;
  let lat = 0;
  let lon = 0;
  const next = (): number => {
    let shift = 0;
    let result = 0;
    let b: number;
    do {
      b = text.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    return result & 1 ? ~(result >> 1) : result >> 1;
  };
  while (i < text.length) {
    lat += next();
    lon += next();
    out.push({ lat: lat / 1e5, lon: lon / 1e5 });
  }
  return out;
}

/** An open ring of vertices approximating a circle around a point. */
export function circle(centre: LatLon, radiusM = SEARCH_RADIUS_M,
                       vertices = SEARCH_VERTICES): LatLon[] {
  const dLat = radiusM / 111320;
  const dLon = dLat / Math.cos((centre.lat * Math.PI) / 180);
  return Array.from({ length: vertices }, (_, k) => {
    const a = (2 * Math.PI * k) / vertices;
    return { lat: centre.lat + dLat * Math.cos(a), lon: centre.lon + dLon * Math.sin(a) };
  });
}

export function streetViewUrl(p: LatLon): string {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=` +
         `${p.lat.toFixed(6)},${p.lon.toFixed(6)}`;
}

export function rightmoveUrl(p: LatLon): string {
  const ring = circle(p);
  const poly = encodePolyline([...ring, ring[0]!]);
  const area = `USERDEFINEDAREA^${JSON.stringify({ polylines: poly })}`;
  return `https://www.rightmove.co.uk/property-for-sale/map.html` +
         `?locationIdentifier=${encodeURIComponent(area)}` +
         `&viewType=MAP&numberOfPropertiesPerPage=95&sortType=6` +
         `&channel=BUY&transactionType=BUY`;
}

export function zooplaUrl(p: LatLon): string {
  const poly = encodePolyline(circle(p));
  return `https://www.zoopla.co.uk/for-sale/map/property/uk?radius=0` +
         `&polyenc=${encodeURIComponent(encodeURIComponent(poly))}`;
}
